import { cn } from "@/lib/utils";
import { CareerModal } from "components/core/modals/CareerModal";
import { DarkModeReleaseModal } from "components/core/modals/DarkModeReleaseModal";
import { SelectInterestTagModal } from "components/core/modals/SelectInterestTagModal";
import { SignInModal } from "components/core/modals/SignInModal";
import { Header } from "components/core/templates/Header";
import { SideBar } from "components/core/templates/SideBar";
import { SideBarTrial } from "components/core/templates/SideBarTrial";
import { ChromeHomeBoard } from "components/domain/chrome-extension/ChromeHomeBoard";
import { AppContext } from "contexts/AppContext";
import { AuthContext } from "contexts/AuthContext";
import { useMixNoticesQuery, useSessionQuery } from "graphql/generated";
import { useDarkModeReleaseModal } from "hooks/useDarkModeReleaseModal";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { usePathCheck } from "hooks/usePathCheck";
import { useScrolled } from "hooks/useScrolled";
import { fireEvent, initTagManager } from "lib/ga";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo } from "react";
import { DEVICE_TYPE } from "utils/enum";
import { getHash } from "utils/getHash";

import { MainBanner, NonDesktopMainBanner } from "./banners/MainBanner";
import { TopBanner } from "./banners/TopBanner";
import { IntervalModal } from "./modals/IntervalModal";
import { Footer } from "./templates/Footer";

// Lazy load AnimatedCursor to reduce initial bundle size (framer-motion is heavy)
const AnimatedCursor = dynamic(
    () =>
        import("../animated/AnimatedCursor").then((mod) => mod.AnimatedCursor),
    { ssr: false } // Cursor doesn't need SSR, only works on client
);

export const Layout = ({
    children,
    browserName,
    deviceType
}: {
    children: React.ReactNode;
    browserName: string;
    deviceType: DEVICE_TYPE;
}) => {
    const router = useRouter();
    const { isFeatureEnabled, loading: featureFlagsLoading } =
        useFeatureFlags();

    const isHomePage = usePathCheck("/");
    const isTrialPage = usePathCheck("/trial");
    const isOriginalContentPage = usePathCheck("/contents/original/[id]");
    const isLecturePage = usePathCheck("/course/[id]");
    const isPaymentsRoute = usePathCheck("/payments*");
    const isGoodbyePage = usePathCheck("/goodbye");
    const isMaintenancePage = usePathCheck("/maintenance");
    const isVotePage = usePathCheck("/vote");
    const isMypage = usePathCheck("/mypage");
    const isMypageOrders = usePathCheck("/mypage/orders");
    const isMypageCourses = usePathCheck("/mypage/courses");
    const isMypageRoute = isMypage || isMypageOrders || isMypageCourses;

    // Redirect to maintenance page if maintenance mode is enabled
    useEffect(() => {
        // Guard against SSR
        if (typeof window === "undefined") return;

        if (
            process.env.NEXT_PUBLIC_MAINTENANCE_MODE === "true" &&
            !isMaintenancePage
        ) {
            // For extension builds, use .html extension for static routing
            if (process.env.BUILD_TARGET === "extension") {
                window.location.href = "maintenance.html";
            } else {
                router.replace("/maintenance");
            }
        }
    }, [isMaintenancePage, router]);

    const { data: sessionData, loading, refetch } = useSessionQuery();
    const { data: noticesData, loading: noticeLoading } = useMixNoticesQuery();

    const session = useMemo(() => sessionData?.session ?? null, [sessionData]);
    const hash = useMemo(() => getHash(router.asPath), [router]);

    const scrolled = useScrolled(0);
    const {
        isOpen: isDarkModeModalOpen,
        isLoaded: isDarkModeModalLoaded,
        closeModal: closeDarkModeModal,
        hideForToday: hideDarkModeModalForToday
    } = useDarkModeReleaseModal();

    const onCloseModal = useCallback(async () => {
        await router.push(
            {
                ...router,
                hash: null
            },
            undefined,
            { scroll: false }
        );
    }, [router]);

    useEffect(() => {
        /* GA 정적 매개변수는 loading 이후 세팅 */
        if (loading) return;

        if (session?.me) {
            fireEvent({
                params: {
                    user_id: session.me.id,
                    career: session.me.career,
                    position: session.me.position
                }
            });

            /* crisp */
            (window as any)?.$crisp?.push([
                "set",
                "user:email",
                session.me.email
            ]);
        }

        /* GA Init */
        initTagManager();
    }, [session, loading]);

    // Refetch session data when hash changes to signup-form
    useEffect(() => {
        if (hash === "signup-form" && !session?.me && !loading) {
            refetch();
        }
    }, [hash, session?.me, loading, refetch]);

    return (
        <AppContext.Provider
            value={{
                browserName,
                deviceType
            }}
        >
            <AuthContext.Provider value={{ session, loading, refetch }}>
                <AnimatedCursor />
                {isOriginalContentPage ? (
                    <main
                        className="flex min-h-screen flex-col"
                        style={{ backgroundColor: "var(--color-background)" }}
                    >
                        <div className="flex-1">{children}</div>
                    </main>
                ) : isLecturePage ? (
                    <main
                        className="flex flex-col"
                        style={{
                            backgroundColor: "var(--color-background)",
                            height: "80dvh"
                        }}
                    >
                        <div className="flex-1">{children}</div>
                    </main>
                ) : isTrialPage ? (
                    <>
                        {!noticeLoading && (
                            <TopBanner
                                text={noticesData?.mix_notices[0]?.content}
                                url={
                                    noticesData?.mix_notices[0]?.target_url ??
                                    undefined
                                }
                                endDate={
                                    noticesData?.mix_notices[0]?.end_at.split(
                                        "T"
                                    )[0]
                                }
                            />
                        )}
                        <Header
                            type={scrolled ? "border" : "default"}
                            showSearch={true}
                            hideAuth={false}
                            className="mb-[var(--header-margin)]"
                        />
                        <main
                            className="mx-auto max-w-[var(--page-max-width)]"
                            style={{
                                backgroundColor: "var(--color-background)"
                            }}
                        >
                            <div
                                className="flex w-full px-[var(--page-padding)]"
                            >
                                <SideBarTrial className="hidden lg:block" />
                                <div
                                    className={cn(
                                        "w-full pb-16",
                                        isFeatureEnabled(
                                            "UI_HEADER_DROPDOWN"
                                        ) && "pb-0"
                                    )}
                                >
                                    {children}
                                </div>
                            </div>
                        </main>
                    </>
                ) : isPaymentsRoute || isGoodbyePage ? (
                    <main
                        className="flex min-h-screen flex-col"
                        style={{ backgroundColor: "var(--color-background)" }}
                    >
                        <Header
                            showSearch={false}
                            hideAuth={false}
                            hideThemeToggle={false}
                            className="mb-[var(--header-margin)] border-b
                                border-text-focus-visible
                                bg-[var(--color-background)]"
                        />
                        <div className="flex-1 px-[var(--page-padding)]">
                            {children}
                        </div>
                    </main>
                ) : isMaintenancePage ? (
                    <main
                        className="flex min-h-screen flex-col"
                        style={{ backgroundColor: "var(--color-background)" }}
                    >
                        <Header
                            showSearch={true}
                            hideAuth={true}
                            className="h-14 px-6 py-3"
                        />
                        {children}
                    </main>
                ) : isVotePage ? (
                    <main
                        className="flex min-h-screen flex-col"
                        style={{ backgroundColor: "var(--color-background)" }}
                    >
                        {children}
                    </main>
                ) : (
                    <>
                        {!noticeLoading && (
                            <TopBanner
                                text={noticesData?.mix_notices[0]?.content}
                                url={
                                    noticesData?.mix_notices[0]?.target_url ??
                                    undefined
                                }
                                endDate={
                                    noticesData?.mix_notices[0]?.end_at.split(
                                        "T"
                                    )[0]
                                }
                            />
                        )}
                        <Header
                            type={scrolled ? "border" : "default"}
                            showSearch={true}
                            hideAuth={false}
                            className="mb-[var(--header-margin)]"
                        />
                        <main
                            className="mx-auto max-w-[var(--page-max-width)]"
                            style={{
                                backgroundColor: "var(--color-background)"
                            }}
                        >
                            {isHomePage &&
                                !router.query.tag &&
                                (process.env.BUILD_TARGET === "extension" ? (
                                    <>
                                        <ChromeHomeBoard />
                                        <NonDesktopMainBanner />
                                    </>
                                ) : (
                                    <MainBanner />
                                ))}
                            <div
                                className="flex w-full px-[var(--page-padding)]"
                            >
                                {(() => {
                                    if (featureFlagsLoading) {
                                        console.log(
                                            "Layout - feature flags still loading"
                                        );
                                        return null; // Don't render navigation while loading
                                    }
                                    const flagValue =
                                        isFeatureEnabled("UI_HEADER_DROPDOWN");
                                    const shouldShow =
                                        !flagValue || isMypageRoute;
                                    console.log(
                                        "Layout - UI_HEADER_DROPDOWN:",
                                        flagValue,
                                        "shouldShow SideBar:",
                                        shouldShow
                                    );
                                    return (
                                        shouldShow && (
                                            <SideBar className="hidden lg:flex" />
                                        )
                                    );
                                })()}
                                <div
                                    className={cn(
                                        "w-full pb-16",
                                        isFeatureEnabled(
                                            "UI_HEADER_DROPDOWN"
                                        ) && "pb-0"
                                    )}
                                >
                                    {children}
                                </div>
                            </div>
                        </main>
                    </>
                )}
                {isFeatureEnabled("UI_HEADER_DROPDOWN") && <Footer />}
                {hash === "signin" && (
                    <SignInModal
                        isOpen={true}
                        onRequestClose={onCloseModal}
                        close={onCloseModal}
                    />
                )}
                {hash === "signup-form" && session?.me && (
                    <CareerModal
                        me={session.me}
                        isOpen={true}
                        onRequestClose={onCloseModal}
                        close={onCloseModal}
                    />
                )}
                {hash === "interest-tags" && (
                    <SelectInterestTagModal
                        isOpen={true}
                        onRequestClose={onCloseModal}
                    />
                )}
                {/* <SurveyModal
          endDate="2023-09-09"
          isOpen={showSurveyModal}
          close={() => setShowSurveyModal(false)}
        /> */}
                <IntervalModal
                    interval={1}
                    startDate="2025-08-10"
                    endDate="2025-08-22"
                    targetPages={["/"]}
                    isVisibleInExtension={false}
                />
                {isDarkModeModalLoaded && (
                    <DarkModeReleaseModal
                        isOpen={isDarkModeModalOpen}
                        onRequestClose={closeDarkModeModal}
                        onHideForToday={hideDarkModeModalForToday}
                    />
                )}
            </AuthContext.Provider>
        </AppContext.Provider>
    );
};
